import React, {useCallback, useEffect, useMemo, useState} from "react";
import {FileRejection, useDropzone} from "react-dropzone";

import "./InvoiceForm.scss";
import {fileKey, formatBytes, openFileInNewTab} from "@pages/Invoice/Components/InvoiceForm/InvoiceFormUtils";
import {ChevronRightIcon} from "@components/Icons/ChevronRightIcon";
import {CmsSection, SECTION_TYPE} from "@lib/Cms/CmsModels";
import {findFirstSection} from "@lib/Cms/CmsUtils";
import {useProjectTranslations} from "@i18n/i18nUtils";
import {useModalStore} from "@store/ModalStore";
import EventService from "@lib/Event/EventService";
import {useShallow} from "zustand/react/shallow";
import ContentIoService from "@lib/ContentIo/ContentIoService";
import {
    CONTENTIO_ACCESS_MODIFIER,
    CONTENTIO_MODULE_ENUM,
    CONTENTIO_SUBMODULE_ENUM
} from "@lib/ContentIo/ContentIoModels";
import {Event, EVENT_CONFIGURATION_STATUS_ENUM, SendEventRequest} from "@lib/Event/EventModels";
import {randomString} from "@commons/Utils";
import {useUserPointsCap} from "@lib/PointsCap/PointsCapHooks";

const MAX_FILES = 5;
const MAX_SIZE = 3.5 * 1024 * 1024;

const FILES_MODAL_KEY = "invoice-files";
const UPLOADING_MODAL_KEY = "invoice-uploading";

type UploadItem = {
    key: string;
    file: File;
};

type UploadState =
    | { status: "idle" }
    | { status: "uploading" }
    | { status: "success" }
    | { status: "screening_error" }
    | { status: "generic_error" };

interface InvoiceFormProps {
    cms: CmsSection;
    event: Event;
}

export default function InvoiceForm({cms, event}: InvoiceFormProps) {
    const [items, setItems] = useState<UploadItem[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [uploadState, setUploadState] = useState<UploadState>({status: "idle"});

    const {t} = useProjectTranslations();

    const {data: pointsCap} = useUserPointsCap();
    const capReached = pointsCap?.isCapTargetReached ?? false;

    const cmsCta = cms.cta?.[0];
    const dropzoneSection = useMemo(
        () => findFirstSection(cms?.sections, SECTION_TYPE.DROPZONE) ?? {},
        [cms]
    );
    const uploadedSection = useMemo(
        () => findFirstSection(cms?.sections, SECTION_TYPE.UPLOADED_MODAL) ?? {},
        [cms]
    );

    const counterLabel = `${items.length}/${MAX_FILES}`;

    const {openModal, closeModal} = useModalStore(
        useShallow((s) => ({
            closeModal: s.close,
            openModal: s.open,
        }))
    );

    const removeItem = useCallback((key: string) => {
        setItems((prev) => prev.filter((p) => p.key !== key));
    }, []);

    const onDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            const nextErrors: string[] = [];

            for (const rej of fileRejections) {
                const fname = rej.file?.name ?? "File";
                for (const e of rej.errors) {
                    if (e.code === "file-too-large") {
                        nextErrors.push(
                            t("invoice:form:validation:file_too_big", {
                                fileName: fname,
                                maxSize: formatBytes(MAX_SIZE),
                            })
                        );
                    } else if (e.code === "file-invalid-type") {
                        nextErrors.push(
                            t("invoice:form:validation:format_not_supported", {
                                fileName: fname,
                            })
                        );
                    } else {
                        nextErrors.push(`${fname}: ${e.message}`);
                    }
                }
            }

            setItems((prev) => {
                const prevKeys = new Set(prev.map((p) => p.key));
                const merged = [...prev];

                for (const f of acceptedFiles) {
                    const key = fileKey(f);

                    if (prevKeys.has(key)) continue;

                    if (merged.length >= MAX_FILES) {
                        nextErrors.push(
                            t("invoice:form:validation:too_many_files", {maxCount: MAX_FILES})
                        );
                        break;
                    }

                    merged.push({key, file: f});
                    prevKeys.add(key);
                }

                return merged;
            });

            setErrors(nextErrors);
        },
        [t]
    );

    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop,
        disabled: capReached,
        noClick: false,
        noKeyboard: false,
        multiple: true,
        maxSize: MAX_SIZE,
        accept: {
            "image/*": [],
            "application/pdf": [],
        },
    });

    const FilesModalContent = useCallback(() => {
        return <UploadedFilesList items={items} uploadedSection={uploadedSection} onRemove={removeItem}/>;
    }, [items, uploadedSection, removeItem]);

    const showFilesPreviewModal = useCallback(async () => {
        return openModal({
            key: FILES_MODAL_KEY,
            tone: "neutral",
            title: `${uploadedSection.title ?? ""} (${items.length}/${MAX_FILES})`,
            content: () => <FilesModalContent/>,
            primaryAction: {label: t("close")},
            isDismissable: true,
        });
    }, [openModal, uploadedSection.title, items.length, t, FilesModalContent]);

    useEffect(() => {
        useModalStore.getState().update(
            {
                title: `${uploadedSection.title ?? ""} (${items.length}/${MAX_FILES})`,
                content: () => <FilesModalContent/>,
            },
            FILES_MODAL_KEY
        );
    }, [items.length, uploadedSection.title, FilesModalContent]);

    const showUploadFeedback = useCallback(
        async (state: UploadState) => {
            switch (state.status) {
                case "uploading":
                    return openModal({
                        key: UPLOADING_MODAL_KEY,
                        tone: "neutral",
                        title: t("invoice:form:feedback:uploading:title"),
                        content: <p className="tp-xs">{t("invoice:form:feedback:uploading:message")}</p>,
                        primaryAction: {
                            label: t("invoice:form:feedback:uploading:label"),
                            disabled: true,
                            autoClose: false,
                        },
                        isDismissable: false,
                        showCloseButton: false,
                    });

                case "success":
                    return openModal({
                        tone: "success",
                        title: t("invoice:form:feedback:upload_completed:title"),
                        content: <p className="tp-xs">{t("invoice:form:feedback:upload_completed:message")}</p>,
                        primaryAction: {label: t("invoice:form:feedback:upload_completed:label")},
                    });

                case "screening_error":
                    return openModal({
                        tone: "error",
                        title: t("invoice:form:feedback:screening_error:title"),
                        content: <p className="tp-xs">{t("invoice:form:feedback:screening_error:message")}</p>,
                        secondaryAction: {label: t("invoice:form:feedback:screening_error:label_secondary")},
                        primaryAction: {label: t("invoice:form:feedback:screening_error:label")},
                    });

                case "generic_error":
                    return openModal({
                        tone: "error",
                        title: t("invoice:form:feedback:generic_error:title"),
                        content: <p className="tp-xs">{t("invoice:form:feedback:generic_error:message")}</p>,
                        primaryAction: {label: t("invoice:form:feedback:generic_error:label")},
                    });

                default:
                    return openModal({
                        tone: "neutral",
                        title: "Status",
                        primaryAction: {label: t("close")},
                    });
            }
        },
        [openModal, t]
    );

    const closeUploadingModal = useCallback(() => {
        closeModal("dismiss");
    }, [closeModal]);

    const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault();
        setErrors([]);

        if (capReached) return;

        if (items.length === 0) {
            setErrors([t("invoice:form:validation:no_files")]);
            return;
        }

        if (!cms.options || !cms.options.filesKey || !cms.options.primaryKey) {
            setErrors([t("invoice:form:validation:no_options")]);
            return;
        }

        const files = items.map((i) => i.file);

        setUploadState({status: "uploading"});
        void showUploadFeedback({status: "uploading"});

        try {
            const uploadResults = await ContentIoService.manyUpload(files, {
                accessModifier: CONTENTIO_ACCESS_MODIFIER.PRIVATE,
                destination: "default",
                moduleName: CONTENTIO_MODULE_ENUM.JNEXT,
                middlePath: CONTENTIO_SUBMODULE_ENUM.UPLOADS,
                subDir: "invoices",
            });

            const failed = uploadResults.filter((r) => !r.uploaded);
            if (failed.length > 0) {
                setUploadState({status: "generic_error"});
                closeUploadingModal();
                await showUploadFeedback({status: "generic_error"});
                setUploadState({status: "idle"});
                return;
            }

            const fileRefs = uploadResults.map((r) => r.fileRef);

            /**
             * 1) Check files (screening)
             */
            const filesAreValid = await EventService.checkFiles(fileRefs);

            if (!filesAreValid) {
                setUploadState({status: "screening_error"});
                closeUploadingModal();

                const result = await showUploadFeedback({status: "screening_error"});
                if (result === "primary") {
                    await showFilesPreviewModal();
                }

                setUploadState({status: "idle"});
                return;
            }

            /**
             * 2) Send event
             */
            const payload: SendEventRequest = {
                extraEventPayload: {
                    fields: [
                        {
                            hurlId: cms.options?.primaryKey ?? "key",
                            type: "JTEXT",
                            values: [randomString()],
                        },
                        {
                            hurlId: cms.options?.filesKey ?? "files",
                            type: "JFILE",
                            values: [...fileRefs],
                        },
                    ],
                },
            };

            await EventService.sendEvent(event.logicId, payload);

            setUploadState({status: "success"});
            closeUploadingModal();
            await showUploadFeedback({status: "success"});

            setItems([]);
            setErrors([]);
            setUploadState({status: "idle"});
            return;
        } catch (err) {
            console.log("Invoice upload flow failed:", err);

            setUploadState({status: "generic_error"});
            closeUploadingModal();
            await showUploadFeedback({status: "generic_error"});
            setUploadState({status: "idle"});
        }
    };

    const eventIsOpened = [EVENT_CONFIGURATION_STATUS_ENUM.OPEN, EVENT_CONFIGURATION_STATUS_ENUM.CLOSING].includes(
        event.eventConfigurationStatus
    );

    return (
        <form
            action=""
            className={`invoice-form ${eventIsOpened ? "enabled" : "disabled"}`}
            aria-disabled={!eventIsOpened}
            onSubmit={handleSubmit}
        >
            <div className="invoice-form__upload-area">
                <div className="invoice-form__dropzone">
                    <div
                        {...getRootProps({
                            className: `dropArea ${isDragActive ? "isDragActive" : ""} ${capReached ? "isDisabled" : ""}`,
                            role: "button",
                            tabIndex: -1,
                        })}
                    >
                        <input {...getInputProps()} />

                        <div className="hint">
                            <p className={"tp-s light color-black"}>
                                {isDragActive ? (
                                    <>{dropzoneSection.options?.hint_drop ?? ""}</>
                                ) : (
                                    <>
                                        {dropzoneSection.options?.pt1 ?? ""}{" "}
                                        <span className="dropzoneLink tp-xs color-primary" aria-hidden="true">
                                          {dropzoneSection.options?.pt2 ?? ""}
                                        </span>{" "}
                                        {dropzoneSection.options?.pt3 ?? ""}
                                    </>
                                )}
                            </p>

                            <p className={"tp-xs light color-black"}>{dropzoneSection.options?.hint_formats ?? ""}</p>
                        </div>
                    </div>

                    <div className="hintMeta">
                        <div className="hintMeta__highlight">
                            <p className={"tp-2xs"}>{t("invoice:form:invoices_notice")}</p>
                        </div>
                    </div>

                    <div className="hintMeta">
                        <span className={"tp-xxs light color-black"}>{dropzoneSection.options?.hint_size ?? ""}</span>
                        <span className={"tp-xxs semibold color-black"}>{counterLabel}</span>
                    </div>

                    {items.length > 0 && (
                        <button
                            type="button"
                            className="hintManage tp-xs color-primary"
                            onClick={(ev) => {
                                ev.preventDefault();
                                ev.stopPropagation();
                                void showFilesPreviewModal();
                            }}
                        >
                            {uploadedSection.options?.viewFiles ?? ""}
                        </button>
                    )}

                    {errors.length > 0 && (
                        <div className="invoice-form__errors">
                            {errors.map((err, idx) => (
                                <p key={idx} className="tp-2xs color-primary">
                                    - {err}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {capReached ? (
                <div className="invoice-form__cap-reached">
                    <p className="tp-xs color-primary">{t("invoice:form:cap_reached")}</p>
                </div>
            ) : (
                <div className="invoice-form__actions">
                    <button className={"cta cta--medium"} type="submit"
                            disabled={items.length === 0 || uploadState.status === "uploading"}>
                        <span className="label">
                            {uploadState.status === "uploading" ? cmsCta?.options?.loading ?? "Uploading..." : cmsCta?.title ?? "Upload"}
                        </span>
                        <span className="icon">
                            <ChevronRightIcon/>
                        </span>
                    </button>
                </div>
            )}
        </form>
    );
}

function UploadedFilesList({
                               items,
                               uploadedSection,
                               onRemove,
                           }: {
    items: UploadItem[];
    uploadedSection: any;
    onRemove: (key: string) => void;
}) {
    const [thumbs, setThumbs] = useState<Record<string, string>>({});

    useEffect(() => {
        const next: Record<string, string> = {};
        const created: string[] = [];

        for (const it of items) {
            if (it.file.type.startsWith("image/")) {
                const url = URL.createObjectURL(it.file);
                next[it.key] = url;
                created.push(url);
            }
        }

        setThumbs(next);

        return () => {
            created.forEach((u) => URL.revokeObjectURL(u));
        };
    }, [items]);

    return (
        <div className="uploadedList">
            {items.map((it) => {
                const isImage = it.file.type.startsWith("image/");
                const thumb = thumbs[it.key];

                return (
                    <div key={it.key} className="uploadedRow">
                        <div className="uploadedThumb">
                            {isImage && thumb ? <img src={thumb} alt=""/> : <div className="uploadedPdf">PDF</div>}
                        </div>

                        <div className="uploadedMeta">
                            <div className="uploadedName tp-2xs semibold" title={it.file.name}>
                                {it.file.name}
                            </div>
                            <div className="uploadedSize tp-2xs regular">{formatBytes(it.file.size)}</div>
                        </div>

                        <div className="uploadedActions">
                            <button type="button" className="tp-xs" onClick={() => openFileInNewTab(it.file)}>
                                {uploadedSection.options?.openFile ?? "Open"}
                            </button>
                            <button type="button" className="tp-xs" onClick={() => onRemove(it.key)}>
                                {uploadedSection.options?.removeFile ?? "Remove"}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}